import * as st from './st';
import { SLOT_IDX } from './slotIdx';
import * as characterUtils from '../core/characterUtils';
import { processItemRequirementText } from '../core/statProcessing';
import { TYPES } from './buildUtils';
import { itemEffectNumericValue } from './itemEffectValue';
import {
    normalizeWeaponMagingElement,
    weaponHasNeutralDamageForMaging,
    weaponHasNeutralHealForMaging,
} from './weaponMaging';
import {
    DEFAULT_FM_EXOS,
    FM_EXO_IDS,
    fmExosFromShortcuts,
    forgeShortcutExos,
    isSlotMagable,
    magedEffectsMap,
    normalizeItemMaging,
    TRANS_LINE,
    TRANSCENDENCE_DEFAULT_LEVEL,
    withLine,
    magingBonusExcess,
    magingDelta,
    lineKind,
    MAGING_KIND,
} from './itemMaging';
import { normTagIds } from './criteriaTags';
import { BEST_WEAPON_METRIC_DEFAULT, normalizeBestWeaponMetric } from './bestWeaponMetric';
import {
    boostEffectsMap,
    pickedItemBoostIds,
    normalizeBoostCharges,
    normalizeBoostSelection,
    setBoostCharges,
    setBoostOptions,
    withoutOtherClassBoosts,
} from './boosts';
import { getTypeId } from './spellUtils';
import { CHARAC_IDS } from './localSolve/kernelRequest';
import { normalizeWeightMap, statWeightKey, WEIGHT_DEFAULT } from './criteriaWeights';
import { calculateBlendedColor } from '../components/utils/ElementalColor';
import { peekItemCatalog } from '../services/itemCatalog';
import { deriveSetBonuses } from './setBonusesFromCatalog';
import { localizedText } from '../utils/localizedText';

const TOTAL_SLOTS = 16; // The total number of equipment slots (0-15)
/** The four elements whose totals set how strongly a card's tint is applied. */
const ELEMENT_STAT_IDS = [st.STRENGTH_ID, st.INTELLIGENCE_ID, st.CHANCE_ID, st.AGILITY_ID];
const TURQUOISE_DOFUS_ITEM_ID = 739;
/** Characteristics that can be scrolled (parchoted) to their +100 cap. Power is not one. */
const SCROLLABLE_STAT_IDS = st.STATS
    .filter(stat => stat.category === 'attribute' && stat.id !== st.POWER_ID)
    .map(stat => stat.id);
/** Flat bonus a fully scrolled characteristic grants. */
export const SCROLL_VALUE = 100;
const TURQUOISE_FINAL_DAMAGE_MAX = 7;



/** Rows the stat tooltip shows for the points a build has before any item. */
const TXT_BASE = { fr: 'Base', en: 'Base', de: 'Basis', es: 'Base', pt: 'Base' };
const TXT_SCROLLS = { fr: 'Parchemins', en: 'Scrolls', de: 'Schriftrollen', es: 'Pergaminos', pt: 'Pergaminhos' };
const TXT_BOOSTS = { fr: 'Boosts', en: 'Boosts', de: 'Boosts', es: 'Boosts', pt: 'Boosts' };
/** Breakdown row for what `st.GAME_STAT_CAPS` cut off. */
/** Breakdown row for maged AP/MP/Range past the +1 the game allows in total. */
const TXT_MAGING_CAP = {
    fr: 'Limite de forgemagie',
    en: 'Smithmagic limit',
    de: 'Schmiedemagie-Limit',
    es: 'Límite de forjamagia',
    pt: 'Limite de forjamagia',
};
const TXT_GAME_CAP = { fr: 'Limite du jeu', en: 'Game limit', de: 'Spiellimit', es: 'Límite del juego', pt: 'Limite do jogo' };
/** Breakdown row for a set ceiling whose set did not name itself. */
const TXT_SET_CAP = { fr: 'Plafond de panoplie', en: 'Set ceiling', de: 'Set-Obergrenze', es: 'Tope de set', pt: 'Teto de conjunto' };

export const CHARACTER_SEX = {
    MALE: 0,
    FEMALE: 1,
};

function normalizeCharacterSex(raw) {
    return Number(raw) === CHARACTER_SEX.FEMALE ? CHARACTER_SEX.FEMALE : CHARACTER_SEX.MALE;
}

/** How the character is being looked at: which stance, and which way it faces.
 *
 *  Both are kept loose on purpose. The stance names and the eight facings are
 *  the renderer's vocabulary, not this model's, and a build saved under one set
 *  should not become invalid when that set changes — an unknown value simply
 *  falls back to the class default when it is next shown.
 */
function normalizeCharacterStance(raw) {
    return typeof raw === 'string' && raw ? raw : null;
}

function normalizeCharacterFacing(raw) {
    // Absence has to be rejected before coercion: `Number(null)` is 0, and 0 is
    // a real facing, so every build that had never been turned would come back
    // pointing that way.
    if (typeof raw !== 'number' && (typeof raw !== 'string' || raw === '')) return null;
    const facing = Number(raw);
    return Number.isInteger(facing) && facing >= 0 && facing <= 7 ? facing : null;
}

/**
 * Two shapes are valid, and both stay valid.
 *
 * `dataUrl` is a base64 PNG carried inline; `url` points at the same image in
 * R2. Saving externalizes an inline render to a URL, but rows nobody has
 * re-saved keep theirs inline, and a freshly rendered character is inline until
 * it reaches the server. Reading both is what lets those coexist.
 */
function normalizeCharacterRender(raw) {
    if (!raw) return null;
    if (typeof raw === 'string') {
        return raw.startsWith('data:image/') ? { key: null, dataUrl: raw, url: null } : null;
    }
    if (typeof raw !== 'object') return null;
    const rawDataUrl = typeof raw.dataUrl === 'string' ? raw.dataUrl : '';
    const rawUrl = typeof raw.url === 'string' ? raw.url : '';
    const dataUrl = rawDataUrl.startsWith('data:image/') ? rawDataUrl : null;
    const url = rawUrl.startsWith('https://') ? rawUrl : null;
    if (!dataUrl && !url) return null;
    const key = typeof raw.key === 'string' && raw.key ? raw.key : null;
    return { key, dataUrl, url };
}

/** The <img src> for a render, whichever shape it arrived in. */
export function characterRenderSrc(render) {
    const normalized = normalizeCharacterRender(render);
    if (!normalized) return null;
    return normalized.url || normalized.dataUrl || null;
}

/**
 * Everything needed to tint a build card: the blended elemental colour and the
 * elemental stat total that sets how strongly it is applied.
 *
 * Both are pure functions of the stat sheet, and deriving that sheet needs every
 * item's effects — which is why a community card used to drag 16 full item
 * snapshots along purely to paint a gradient. Storing the tint lets the listing
 * ship neither. Null is always valid: `getElementTint()` falls back to
 * computing, so builds saved before this, and any future change to the colour
 * formula, both stay correct.
 */
function normalizeElementTint(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const channel = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return null;
        return Math.max(0, Math.min(255, Math.round(n)));
    };
    const r = channel(raw.r);
    const g = channel(raw.g);
    const b = channel(raw.b);
    if (r === null || g === null || b === null) return null;
    const total = Number(raw.elementTotal);
    return { r, g, b, elementTotal: Number.isFinite(total) ? Math.max(0, total) : 0 };
}

/**
 * The class, from whatever the payload carries.
 *
 * A community card is sent the class *name* only — the full object is `cover`,
 * `icon`, `img_f`, `img_m` and five languages of label, all of it already in
 * CLASSES on this side, so shipping it per card was ~240 bytes of data the
 * browser owns a copy of. A whole object still arrives from every other path and
 * is passed through untouched.
 */
function resolveCharacterClass(raw) {
    if (!raw) return raw;
    const name = typeof raw === 'string' ? raw : raw.name;
    // Already whole: anything with an icon came from a full payload.
    if (typeof raw === 'object' && raw.icon) return raw;
    if (!name) return raw;
    return characterUtils.getCharacterClass(name) || raw;
}

/**
 * The id of a slot entry, whichever shape it is in — a bare id, a stored item
 * object, or nothing. Null means an empty slot.
 */
function resolveItemId(entry) {
    if (typeof entry === 'number') return Number.isFinite(entry) && entry > 0 ? entry : null;
    const id = Number(entry?.id);
    return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * The exos generations place on items, from the forgemagie menu. A build saved
 * before that menu has none stored, only `legacyExos` — the +1 shortcuts the
 * stats panel had then, which were what generations placed — so they stand in.
 */
function normalizeFmExos(raw, legacyExos) {
    if (Array.isArray(raw)) return FM_EXO_IDS.filter((id) => raw.map(Number).includes(id));
    if (!legacyExos || typeof legacyExos !== 'object') return [...DEFAULT_FM_EXOS];
    return fmExosFromShortcuts(legacyExos);
}

function normalizeMaximizeTargets(raw) {
    if (!raw || typeof raw !== 'object') return {};
    const out = {};
    Object.keys(raw).forEach((k) => {
        const id = Number(k);
        if (Number.isFinite(id) && raw[k]) out[id] = true;
    });
    return out;
}

function normalizeMaximizeSpellIds(raw) {
    if (!Array.isArray(raw)) return [];
    const set = new Set();
    raw.forEach((x) => {
        const n = Number(x);
        if (Number.isFinite(n)) set.add(n);
    });
    return Array.from(set).sort((a, b) => a - b);
}

function normalizeMaximizeSpellCriteria(raw) {
    if (!Array.isArray(raw)) return [];
    const out = [];
    const seen = new Set();
    raw.forEach((entry) => {
        let spellId = null;
        let lineKeys = [];
        if (entry && typeof entry === 'object') {
            const rawId = entry.spell_id ?? entry.spellId ?? entry.id;
            const n = Number(rawId);
            if (Number.isFinite(n)) spellId = n;
            if (Array.isArray(entry.line_keys)) lineKeys = entry.line_keys;
            else if (Array.isArray(entry.lineKeys)) lineKeys = entry.lineKeys;
            else if (entry.line_key != null) lineKeys = [entry.line_key];
            else if (entry.lineKey != null) lineKeys = [entry.lineKey];
        }
        if (!Number.isFinite(spellId)) return;
        const cleanKeys = Array.from(new Set(
            (lineKeys || [])
                .map((x) => String(x || '').trim())
                .filter(Boolean)
        )).sort();
        if (!cleanKeys.length) return;
        const sig = `${spellId}:${JSON.stringify(cleanKeys)}`;
        if (seen.has(sig)) return;
        seen.add(sig);
        out.push({ spell_id: spellId, line_keys: cleanKeys });
    });
    return out.sort((a, b) => {
        if (a.spell_id !== b.spell_id) return a.spell_id - b.spell_id;
        return a.line_keys.join(',').localeCompare(b.line_keys.join(','));
    });
}

/** `{ spellId: { lineKey: n } }` with positive whole counts only. */
function normalizeSpellCharges(raw) {
    if (!raw || typeof raw !== 'object') return {};
    const out = {};
    for (const [spellId, counts] of Object.entries(raw)) {
        if (!Number.isFinite(Number(spellId)) || !counts || typeof counts !== 'object') continue;
        const clean = {};
        for (const [lineKey, n] of Object.entries(counts)) {
            const count = Math.trunc(Number(n));
            if (lineKey && Number.isFinite(count) && count >= 1) clean[lineKey] = count;
        }
        if (Object.keys(clean).length) out[String(Number(spellId))] = clean;
    }
    return out;
}

function normalizeMaximizeWeapon(raw) {
    return raw === true;
}

function getItemDisplayEffectsMap(item) {
    const map = item?.effects_map;
    if (!map || typeof map !== 'object') return null;
    const out = {};
    Object.entries(map).forEach(([rawId, rawValue]) => {
        const id = Number(rawId);
        const value = Number(rawValue);
        if (!Number.isFinite(id) || !Number.isFinite(value) || value === 0) return;
        out[id] = value;
    });
    return out;
}

function getEffectLinesMap(effects) {
    if (!Array.isArray(effects)) return null;
    const out = {};
    effects.forEach((effect) => {
        if (!effect) return;
        const characId = Number(effect.characteristic);
        if (!Number.isFinite(characId)) return;
        const value = itemEffectNumericValue(effect);
        if (!Number.isFinite(value) || value === 0) return;
        out[characId] = (out[characId] || 0) + value;
    });
    return Object.keys(out).length ? out : null;
}

/**
 * An item's stat lines as `{ [characteristicId]: value }` — exactly the map
 * `_calculateStats` folds into the sheet below, `effects_map` first and the
 * legacy `effects` array as the fallback.
 *
 * Exported because pricing a *swap* has to add precisely what equipping would:
 * see `createWeaponSheetFactory` in core/weaponDamage.js, which builds the sheet
 * a candidate weapon would be swung on without rebuilding the whole Build.
 */
export function itemEffectsMap(item) {
    if (!item) return null;
    return getItemDisplayEffectsMap(item) || getEffectLinesMap(item.effects);
}

/** Passive overrides with every item in `itemIds` switched off. */
function withPickedItemBoostsDisabled(passiveOverrides, itemIds) {
    if (!itemIds?.size) return passiveOverrides;
    // Per-line keys ("739:0") win over the item's own key, so they go too.
    const out = Object.fromEntries(Object.entries(passiveOverrides)
        .filter(([key]) => !itemIds.has(Number(String(key).split(':')[0]))));
    itemIds.forEach((id) => { out[String(id)] = { enabled: false }; });
    return out;
}

function getPassiveOverride(passiveOverrides, itemId, index) {
    if (!passiveOverrides || typeof passiveOverrides !== 'object') return null;
    const key = `${Number(itemId)}:${Number(index)}`;
    const override = passiveOverrides[key] ?? passiveOverrides[String(itemId)];
    return override && typeof override === 'object' ? override : null;
}

function getPassiveEffectLinesMap(item, passiveOverrides) {
    if (!Array.isArray(item?.passive_effects)) return 0;
    const out = {};
    item.passive_effects.forEach((effect, index) => {
        const override = getPassiveOverride(passiveOverrides, item.id, index);
        if (override?.enabled === false) return;
        if (!effect) return;
        const characId = Number(effect.characteristic);
        if (!Number.isFinite(characId)) return;
        let value = itemEffectNumericValue(effect);
        if (override && Object.prototype.hasOwnProperty.call(override, 'value')) {
            const overrideValue = Number(override.value);
            if (Number.isFinite(overrideValue)) value = overrideValue;
        }
        if (!Number.isFinite(value) || value === 0) return;
        out[characId] = (out[characId] || 0) + value;
    });
    return Object.keys(out).length ? out : null;
}

function getPassiveEffectValue(item, statId, passiveOverrides) {
    if (!Array.isArray(item?.passive_effects)) return 0;
    return item.passive_effects.reduce((sum, effect, index) => {
        const override = getPassiveOverride(passiveOverrides, item.id, index);
        if (override?.enabled === false) return sum;
        if (Number(effect?.characteristic) !== Number(statId)) return sum;
        let value = itemEffectNumericValue(effect);
        if (override && Object.prototype.hasOwnProperty.call(override, 'value')) {
            const overrideValue = Number(override.value);
            if (Number.isFinite(overrideValue)) value = overrideValue;
        }
        return Number.isFinite(value) ? sum + value : sum;
    }, 0);
}

function turquoiseFinalDamageForCritical(critical, maxValue = TURQUOISE_FINAL_DAMAGE_MAX) {
    const crit = Math.max(0, Math.min(100, Math.trunc(Number(critical) || 0)));
    const max = Number.isFinite(Number(maxValue)) ? Number(maxValue) : TURQUOISE_FINAL_DAMAGE_MAX;
    return Math.floor(((max * crit) + 50) / 100);
}

function addItemStatsFromMap(stats, statMap, item, sourceLabel = null) {
    if (!statMap || typeof statMap !== 'object') return;
    Object.entries(statMap).forEach(([rawCharId, rawValue]) => {
        const characId = Number(rawCharId);
        const value = Number(rawValue);
        if (!Number.isFinite(characId) || !Number.isFinite(value) || value === 0) return;
        if (!stats[characId]) return;
        stats[characId].current += value;
        stats[characId].breakdown.push({
            name: sourceLabel || item.name || { fr: 'Objet', en: 'Item', de: 'Gegenstand', es: 'Objeto', pt: 'Item' },
            value,
        });
    });
}

// Utility to generate a default stat map from STATS, with optional exclusion
function getDefaultStatMap(fallback = 0, excludeIds = []) {
    const map = {};
    st.STATS.forEach(stat => {
        if (!excludeIds.includes(stat.id)) {
            map[stat.id] = fallback;
        }
    });
    return map;
}

/**
 * What a stored `desired` map means. Version 2: a characteristic with no entry
 * has no target, and every number is one — its minimum included, which is how
 * "Resistance ≥ 0" is asked for. Before it, every characteristic was stored at
 * its minimum to mean "no target", so the minimum itself could not be targeted.
 * Payloads without the marker are read the old way; see `normalizeDesired`.
 */
export const DESIRED_VERSION = 2;

/**
 * The stored target map, cleaned: numbers only, off entries dropped. A map
 * from before `DESIRED_VERSION` also loses every entry at or below the stat's
 * minimum, because there that value was the "no target" sentinel.
 */
function normalizeDesired(desired, desiredVersion, lvl) {
    const out = {};
    Object.entries(desired || {}).forEach(([rawId, rawValue]) => {
        const id = Number(rawId);
        if (!Number.isFinite(id) || id === st.LVL_ID) return;
        if (rawValue === null || rawValue === undefined || rawValue === '') return;
        const value = Number(rawValue);
        if (!Number.isFinite(value)) return;
        if (desiredVersion !== DESIRED_VERSION) {
            const statDef = st.STATS.find((s) => s.id === id);
            if (statDef && value <= Build.getEffectiveMinRange(statDef, lvl)) return;
        }
        out[id] = value;
    });
    return out;
}

// SLOT_IDX is imported from ./slotIdx at the top (to avoid a Build <-> buildUtils
// circular import) and re-exported here so existing
// `import { SLOT_IDX } from './Build'` call sites are unaffected.
export { SLOT_IDX };

export default class Build {
    constructor({
        lvl = 200,
        items = Array(TOTAL_SLOTS).fill(null),
        itemIds = null,
        setBonuses = {},
        attributes = {},
        hasScrolls = true,
        scrolls = {},
        // Read only to migrate a build saved before the forgemagie menu.
        exos = undefined,
        desired = {},
        desiredVersion = undefined,
        maximizeTargets = {},
        maximizeSpellIds = [],
        maximizeSpellCriteria = [],
        maximizeWeapon = false,
        maximizeBestWeapon = false,
        bestWeaponMetric = BEST_WEAPON_METRIC_DEFAULT,
        weaponMagingElement = null,
        weaponMagingHealElement = null,
        itemMaging = {},
        transcendence = null,
        fmExos = undefined,
        boosts = {},
        boostCharges = {},
        spellCharges = {},
        characterClass = characterUtils.getCharacterClass('iop'),
        characterSex = CHARACTER_SEX.MALE,
        characterRender = null,
        characterStance = null,
        characterFacing = null,
        criteriaTags = [],
        criteriaWeights = {},
        elementTint = null,
        statSheet = null,
    } = {}) {
        this.lvl = lvl;
        // Saved builds store item *ids* and resolve them against the local
        // catalog; older rows still hold whole item objects. Both shapes arrive
        // here, so both are accepted.
        //
        // The ids are kept separately and are what gets serialized back. If the
        // catalog has not loaded, an id resolves to null and the slot renders
        // empty — but the id survives, so saving such a build round-trips it
        // unchanged instead of quietly emptying sixteen slots.
        //
        // `itemIds` is the source of truth whenever it is supplied, and that is
        // what makes a reconstruction safe. Every mutator rebuilds with
        // `new Build({ ...this })`, whose spread carries the *resolved* `items`
        // — all null while the catalog is cold. Deriving the ids from those
        // would drop the whole equipment, and the next autosave would write
        // sixteen empty slots over the real build.
        //
        // Deriving from `items` when the catalog is merely cold is not enough:
        // the dangerous moment is a build constructed cold and rebuilt once the
        // catalog is warm, where the ids are present but every resolved item is
        // still null. Carrying the ids covers both.
        //
        // Any constructor that empties `items` must empty the ids alongside,
        // or the spread puts the equipment straight back — `setItems`,
        // `removeItem` and `clearBuildSlots` all go through one path that does.
        // Payloads read from storage carry no `itemIds` at all, and are derived
        // from `items`, which may be ids or whole objects.
        const providedIds = Array.isArray(itemIds) ? itemIds.map(resolveItemId) : null;
        this.itemIds = providedIds || items.map(resolveItemId);
        const catalog = this.itemIds.some((id) => id !== null) ? peekItemCatalog() : null;
        this.items = this.itemIds.map((id, slot) => {
            // A slot with no usable id is empty, whatever shape it arrived in.
            // Keeping `items` and `itemIds` in step matters: a truthy entry with
            // no id would count as equipment everywhere that tests a slot for
            // truthiness, right up until a save wrote it back as null.
            if (id === null) return null;
            const entry = items?.[slot];
            // The catalog copy wins; a stored item object is the fallback for
            // rows saved before ids, so those still render with no catalog.
            const stored = entry && typeof entry === 'object' && resolveItemId(entry) === id
                ? entry
                : null;
            const resolved = (catalog ? catalog.itemById.get(id) : null) || stored;
            if (resolved && resolved.item_type_id) {
                const typeInfo = TYPES.find(t => t.id === resolved.item_type_id);
                if (typeInfo) {
                    return { ...resolved, type: typeInfo };
                }
            }
            return resolved || null;
        });
        // Derived from the equipment, not read from the payload. The stored
        // block held every member item of every active set — 5.3 KB a build,
        // and the last large thing in `build_payload` — for data the catalog
        // already answers. See core/setBonusesFromCatalog.
        //
        // The stored block remains the fallback when the catalog is not in
        // memory, which is the only case that can still produce a build whose
        // sets are known but whose items are not. Once the catalog is there it
        // wins outright, including when it derives *nothing*: an empty result
        // means this build has no active set, and deferring to a stale stored
        // block would resurrect one the user has since broken up.
        this.setBonuses = catalog ? deriveSetBonuses(catalog, this.items) : setBonuses;
        const defaultAttrs = getDefaultStatMap(0, [st.LVL_ID]);
        this.attributes = { ...defaultAttrs, ...attributes };

        // Initialize scrolls: if hasScrolls, set every scrollable characteristic to 100
        let initialScrolls = { ...getDefaultStatMap(0, [st.LVL_ID]) };
        if (hasScrolls) {
            SCROLLABLE_STAT_IDS.forEach(statId => {
                initialScrolls[statId] = SCROLL_VALUE;
            });
        }
        this.scrolls = { ...initialScrolls, ...scrolls };

        this.desired = normalizeDesired(desired, desiredVersion, lvl);
        // Carried by every `new Build({ ...this })`, so only a payload read from
        // storage is ever migrated.
        this.desiredVersion = DESIRED_VERSION;
        this.maximizeTargets = normalizeMaximizeTargets(maximizeTargets);
        this.maximizeSpellIds = normalizeMaximizeSpellIds(maximizeSpellIds);
        this.maximizeSpellCriteria = normalizeMaximizeSpellCriteria(
            maximizeSpellCriteria?.length ? maximizeSpellCriteria : []
        );
        this.maximizeWeapon = normalizeMaximizeWeapon(maximizeWeapon);
        if (this.maximizeWeapon && !this.items[SLOT_IDX.WEAPON]) {
            this.maximizeWeapon = false;
        }
        // The two weapon criteria are the same question asked two ways — pin
        // this weapon, or go and find one — so they cannot both be on. The
        // equipped one wins because it names an item the user chose.
        this.maximizeBestWeapon = normalizeMaximizeWeapon(maximizeBestWeapon)
            && !this.maximizeWeapon;
        this.bestWeaponMetric = normalizeBestWeaponMetric(bestWeaponMetric);
        this.weaponMagingElement = normalizeWeaponMagingElement(weaponMagingElement);
        // The heal half of the same forgemage, chosen apart from the damage one:
        // a player mages "soins Neutre" into the element they stack and the
        // damage rows into one they do not, which turns the weapon into
        // something they hit allies with. See core/weaponMaging.js.
        this.weaponMagingHealElement = normalizeWeaponMagingElement(weaponMagingHealElement);
        // Overmage and exo lines per slot, each tied to the item it was forged
        // on: checked against `itemIds` here, so every path that changes a
        // slot's item — equip, remove, a generation — drops it without having
        // to know. See core/itemMaging.js.
        this.itemMaging = normalizeItemMaging(itemMaging, this.itemIds);
        if (exos && typeof exos === 'object') {
            // A build saved while the stats panel had +1 exo shortcuts: each
            // becomes a line on one of its items, so the sheet keeps it. Runs
            // once — saves no longer carry `exos`, nor does `new Build({...this})`.
            this.itemMaging = forgeShortcutExos(this.itemMaging, this.items, this.itemIds, exos);
        }
        // Transcendence runes in generations: true / false once the user has
        // chosen, null to follow the level (see isTranscendenceOn).
        this.transcendence = typeof transcendence === 'boolean' ? transcendence : null;
        // Exos generations place on items (AP / MP / Range ids); each becomes a
        // line on the item it went to, counted like any other.
        this.fmExos = normalizeFmExos(fmExos, exos);
        // Self-buffs the character is assumed to have cast: `{ boostKey: [optionKey] }`.
        // Option keys, not values — what they are worth depends on `lvl`, which
        // moves under them. See core/boosts.js.
        this.boosts = normalizeBoostSelection(boosts);
        // `{ boostKey: { group: n } }` for a buff held n times (Le Portail
        // Eliotrope, per portal); absent means the most it can hold.
        this.boostCharges = normalizeBoostCharges(boostCharges);
        // `{ spellId: { lineKey: n } }`: a charged damage line read at n charges
        // instead of its maximum (core/spellCharges.js). Absent means the maximum.
        this.spellCharges = normalizeSpellCharges(spellCharges);
        this.criteriaTags = normTagIds(criteriaTags);
        this.criteriaWeights = normalizeWeightMap(criteriaWeights);
        this.characterPoints = st.getTotalCharacterPoints(this.lvl);
        this.characterClass = resolveCharacterClass(characterClass);
        this.characterSex = normalizeCharacterSex(characterSex);
        this.characterRender = normalizeCharacterRender(characterRender);
        this.characterStance = normalizeCharacterStance(characterStance);
        this.characterFacing = normalizeCharacterFacing(characterFacing);
        this.elementTint = normalizeElementTint(elementTint);
        this.statSheet = statSheet && typeof statSheet === 'object' ? statSheet : null;
        this._cachedElementTint = null;
        // Sheets by the options that shaped them (see getStats).
        this._statsCache = new Map();
    }

    setItems(items) {
        const oldId = this.items[SLOT_IDX.WEAPON]?.id;
        const newId = items[SLOT_IDX.WEAPON]?.id;
        let weaponMagingElement = this.weaponMagingElement;
        let weaponMagingHealElement = this.weaponMagingHealElement;
        if (String(oldId ?? '') !== String(newId ?? '')) {
            weaponMagingElement = null;
            weaponMagingHealElement = null;
        }
        // Fresh ids alongside the new equipment: the spread still carries the
        // old `itemIds`, and those would otherwise win over this change.
        const newBuild = new Build({
            ...this,
            items,
            itemIds: items.map(resolveItemId),
            weaponMagingElement,
            weaponMagingHealElement,
        });
        return newBuild._recalculateAllSetBonuses();
    }

    setSetBonuses(setBonuses) {
        return new Build({ ...this, setBonuses });
    }

    setLvl(lvl) {
        return new Build({ ...this, lvl });
    }

    setAttributes(attributes) {
        return new Build({ ...this, attributes });
    }

    setScroll(statId, value) {
        const newScrolls = { ...this.scrolls, [statId]: value };
        return new Build({ ...this, scrolls: newScrolls });
    }

    /** True when every scrollable characteristic carries its full +100. */
    isFullyScrolled() {
        return SCROLLABLE_STAT_IDS.every(statId => (this.scrolls[statId] || 0) >= SCROLL_VALUE);
    }

    /** Scroll (100) or unscroll (0) every scrollable characteristic at once. */
    setAllScrolls(scrolled) {
        const value = scrolled ? SCROLL_VALUE : 0;
        const newScrolls = { ...this.scrolls };
        SCROLLABLE_STAT_IDS.forEach(statId => { newScrolls[statId] = value; });
        return new Build({ ...this, scrolls: newScrolls });
    }

    /**
     * Whether generations may put transcendence runes on the gear. The user's
     * choice once they have made one, whatever the level does afterwards;
     * until then on at `TRANSCENDENCE_DEFAULT_LEVEL`, off below it.
     */
    isTranscendenceOn() {
        return this.transcendence ?? this.lvl >= TRANSCENDENCE_DEFAULT_LEVEL;
    }

    /** The user's choice, remembered on the build. */
    setTranscendence(on) {
        return new Build({ ...this, transcendence: !!on });
    }

    /** Whether generations place this exo (AP / MP / Range) on an item. */
    isFmExoOn(statId) {
        return this.fmExos.includes(Number(statId));
    }

    setFmExo(statId, on) {
        const id = Number(statId);
        const rest = this.fmExos.filter((x) => x !== id);
        return new Build({ ...this, fmExos: on ? [...rest, id] : rest });
    }

    /**
     * Set a characteristic's numeric target, or clear it with `null`. Any
     * number is a target, the stat's minimum included.
     */
    setDesired(statId, value) {
        const id = Number(statId);
        const newDesired = { ...this.desired };
        const isOff = value === null || value === undefined || !Number.isFinite(Number(value));
        if (isOff) delete newDesired[id];
        else newDesired[id] = Number(value);
        let nextMaximize = this.maximizeTargets;
        if (this.maximizeTargets[id] && !isOff) {
            nextMaximize = { ...this.maximizeTargets };
            delete nextMaximize[id];
        }
        return new Build({ ...this, desired: newDesired, maximizeTargets: nextMaximize });
    }

    /**
     * Solver "maximize this stat" mode (mutually exclusive with numeric target for same stat).
     * @param {number} statId
     * @param {boolean} enabled
     */
    setMaximizeStat(statId, enabled) {
        const id = Number(statId);
        const statDef = st.STATS.find((s) => s.id === id);
        if (!statDef || id === st.LVL_ID) return this;

        if (!enabled) {
            if (!this.maximizeTargets[id]) return this;
            const next = { ...this.maximizeTargets };
            delete next[id];
            return new Build({ ...this, maximizeTargets: next });
        }

        const newDesired = { ...this.desired };
        delete newDesired[id];
        const newMax = { ...this.maximizeTargets, [id]: true };
        return new Build({ ...this, desired: newDesired, maximizeTargets: newMax });
    }

    /**
     * The user's multiplier for one criterion, keyed with the helpers in
     * core/criteriaWeights.js. 1 when they never touched it.
     * @param {string} key
     * @returns {number}
     */
    getCriterionWeight(key) {
        const w = key ? Number(this.criteriaWeights?.[key]) : NaN;
        return Number.isFinite(w) ? w : WEIGHT_DEFAULT;
    }

    /**
     * Set one criterion's multiplier. ×1 removes the entry rather than storing
     * it, so an untouched criterion costs nothing in the saved build.
     * @param {string} key
     * @param {number} weight
     */
    setCriterionWeight(key, weight) {
        if (!key) return this;
        const next = { ...(this.criteriaWeights || {}) };
        const normalized = Number(weight);
        if (!Number.isFinite(normalized) || normalized === WEIGHT_DEFAULT) delete next[key];
        else next[key] = normalized;
        return new Build({ ...this, criteriaWeights: next });
    }

    /**
     * Drop the multipliers whose key `matches` (all of them by default),
     * putting those criteria back at ×1. The criteria themselves are left
     * alone: this is the weights column's own reset.
     * @param {(key: string) => boolean} [matches]
     * @returns {Build}
     */
    clearCriteriaWeights(matches = () => true) {
        const kept = Object.fromEntries(
            Object.entries(this.criteriaWeights || {}).filter(([key]) => !matches(key))
        );
        if (Object.keys(kept).length === Object.keys(this.criteriaWeights || {}).length) return this;
        return new Build({ ...this, criteriaWeights: kept });
    }

    /**
     * `{ importance }` for a characteristic target, or nothing when the user
     * left it at ×1.
     *
     * Deliberately NOT folded into the rule's `weight`. `weight` is what one
     * point of the characteristic is worth (AP 100, Initiative 0.1) and scales
     * the term; importance is how much the user cares and weights the mean the
     * terms are combined by. Multiplying the term instead would make ×N mean
     * `N^p`, which differs per bucket and vanishes entirely at the spell
     * bucket's p = 0. See solver-core `score::weighted_power_mean`.
     */
    weightRule(statId) {
        const importance = this.getCriterionWeight(statWeightKey(statId));
        if (importance === WEIGHT_DEFAULT) return null;
        return { importance };
    }

    /**
     * Targets payload for the solver (numeric + maximize), keyed by characteristic id.
     *
     * A target at or below the stat's minimum is a **floor** (`floor: true`):
     * "don't let it drop below this", nothing more. It costs the score only while
     * it is missed and is otherwise invisible to it — see solver-core
     * `score::floor_penalty`.
     * @returns {Record<number, object>}
     */
    getSolverTargets() {
        const targets = {};
        const stats = this.getStats();

        Object.keys(this.maximizeTargets || {}).forEach((idStr) => {
            const id = Number(idStr);
            if (!Number.isFinite(id)) return;
            targets[id] = { maximize: true, ...this.weightRule(id) };
        });

        Object.values(stats).forEach((statObj) => {
            if (!statObj || statObj.id === st.LVL_ID) return;
            const id = statObj.id;
            if (this.maximizeTargets[id]) return;
            if (statObj.desired === null || statObj.desired === undefined) return;
            const desired = Number(statObj.desired);
            if (!Number.isFinite(desired)) return;
            const effMin = Build.getEffectiveMinRange(statObj, this.lvl);
            if (desired <= effMin) {
                targets[id] = { min: effMin, floor: true, ...this.weightRule(id) };
                return;
            }
            const isHard = id === st.AP_ID || id === st.MP_ID;
            targets[id] = { min: desired, hard: isHard, ...this.weightRule(id) };
        });

        return targets;
    }

    /** Criteria the solver optimises. A floor only constrains, so it does not count. */
    getSolverTargetCount() {
        return Object.values(this.getSolverTargets()).filter((rule) => !rule.floor).length;
    }

    /**
     * Stat + spell + weapon criteria for enabling generate and auto time limit.
     * @returns {number}
     */
    getCombinedGeneratorTargetCount() {
        return (
            this.getSolverTargetCount()
            + this.getSpellSolverTargetCount()
            + this.getWeaponSolverTargetCount()
        );
    }

    /** @returns {number[]} sorted unique spell ids (DofusDB) to maximize in the solver */
    getMaximizeSpellIds() {
        return [...(this.maximizeSpellIds || [])];
    }

    /** @returns {({spell_id:number,line_keys:string[]}|number)[]} selected spell-line criteria, or legacy spell ids */
    getMaximizeSpellCriteria() {
        const criteria = normalizeMaximizeSpellCriteria(this.maximizeSpellCriteria || []);
        return criteria.length ? criteria : this.getMaximizeSpellIds();
    }

    /**
     * Toggle spell id in maximize list (generator criterion).
     * @param {number} spellId
     */
    toggleMaximizeSpell(spellId) {
        const id = Number(spellId);
        if (!Number.isFinite(id)) return this;
        const set = new Set(this.maximizeSpellIds || []);
        if (set.has(id)) set.delete(id);
        else set.add(id);
        const next = Array.from(set).sort((a, b) => a - b);
        return new Build({ ...this, maximizeSpellIds: next });
    }

    toggleMaximizeSpellLine(criterion) {
        const [clean] = normalizeMaximizeSpellCriteria([criterion]);
        if (!clean) return this;
        const sigOf = (x) => `${x.spell_id}:${JSON.stringify(x.line_keys || [])}`;
        const sig = sigOf(clean);
        const next = normalizeMaximizeSpellCriteria(this.maximizeSpellCriteria || [])
            .filter((x) => sigOf(x) !== sig);
        if (next.length === (this.maximizeSpellCriteria || []).length) next.push(clean);
        return new Build({ ...this, maximizeSpellCriteria: next, maximizeSpellIds: [] });
    }

    /**
     * Set what one boost grants, or clear it with an empty list (Boosts tab).
     *
     * Not a generator criterion: a boost changes what the character *is*, the
     * way scrolls and invested points do, so it feeds the stat sheet and the
     * solver's base stats (getSolverBoostEffects) — a natural stat to both.
     */
    setBoostOptions(boostKey, optionKeys) {
        const next = setBoostOptions(this.boosts, boostKey, optionKeys);
        return new Build({ ...this, boosts: next });
    }

    /**
     * Set how many charges one buff of a boost holds (Boosts tab); its `max`,
     * the default, clears it. `group` picks the buff in a boost holding several.
     */
    setBoostCharges(boostKey, n, max = null, group = 0) {
        return new Build({ ...this, boostCharges: setBoostCharges(this.boostCharges, boostKey, n, max, group) });
    }

    /** Drop every picked boost and its charges (the reset menu). */
    clearBoosts() {
        return new Build({ ...this, boosts: {}, boostCharges: {} });
    }

    /**
     * Read a charged spell line at `n` charges (Spells tab dropdown). `null`
     * or `max` puts it back to the default, the maximum.
     */
    setSpellLineCharges(spellId, lineKey, n, max) {
        const id = String(Number(spellId));
        const counts = { ...(this.spellCharges?.[id] || {}) };
        const count = Math.trunc(Number(n));
        if (Number.isFinite(count) && count >= 1 && !(Number(max) >= 1 && count >= Number(max))) {
            counts[lineKey] = count;
        } else {
            delete counts[lineKey];
        }
        const next = { ...(this.spellCharges || {}) };
        if (Object.keys(counts).length) next[id] = counts;
        else delete next[id];
        return new Build({ ...this, spellCharges: next });
    }

    /** `{ lineKey: n }` for one spell's lines read below their maximum. */
    getSpellLineCharges(spellId) {
        return { ...(this.spellCharges?.[String(Number(spellId))] || {}) };
    }

    /** @returns {Object<string, string[]>} selected option keys per boost */
    getBoosts() {
        return { ...(this.boosts || {}) };
    }

    clearMaximizeSpells() {
        if (!this.maximizeSpellIds?.length && !this.maximizeSpellCriteria?.length) return this;
        return new Build({ ...this, maximizeSpellIds: [], maximizeSpellCriteria: [] });
    }

    toggleMaximizeWeapon() {
        return new Build({
            ...this,
            maximizeWeapon: !this.maximizeWeapon,
            maximizeBestWeapon: false,
        });
    }

    clearMaximizeWeapon() {
        if (!this.maximizeWeapon) return this;
        return new Build({ ...this, maximizeWeapon: false });
    }

    /**
     * Ask the solver to find the weapon, rather than scoring the equipped one.
     *
     * Turning it on drops the equipped-weapon criterion: the two contradict
     * each other, one pinning the slot and the other freeing it.
     */
    toggleMaximizeBestWeapon() {
        return new Build({
            ...this,
            maximizeBestWeapon: !this.maximizeBestWeapon,
            maximizeWeapon: false,
        });
    }

    clearMaximizeBestWeapon() {
        if (!this.maximizeBestWeapon) return this;
        return new Build({ ...this, maximizeBestWeapon: false });
    }

    /**
     * What the find-the-best-weapon criterion ranks candidates on.
     *
     * @param {'ap'|'hit'|'turn'} metric
     */
    setBestWeaponMetric(metric) {
        const next = normalizeBestWeaponMetric(metric);
        if (next === this.bestWeaponMetric) return this;
        return new Build({ ...this, bestWeaponMetric: next });
    }

    getBestWeaponMetric() {
        return this.bestWeaponMetric || BEST_WEAPON_METRIC_DEFAULT;
    }

    /**
     * Forgemage preview element for the equipped weapon's damage and vol rows
     * (null = unmaged).
     * @param {'earth'|'fire'|'water'|'air'|null|string} el
     */
    setWeaponMagingElement(el) {
        const normalized = normalizeWeaponMagingElement(el);
        const w = this.getWeapon()?.weapon;
        if (normalized && !weaponHasNeutralDamageForMaging(w)) {
            return this;
        }
        return new Build({ ...this, weaponMagingElement: normalized });
    }

    getWeaponMagingElement() {
        return this.weaponMagingElement ?? null;
    }

    /**
     * Same, for the equipped weapon's neutral "soins" rows.
     * @param {'earth'|'fire'|'water'|'air'|null|string} el
     */
    setWeaponMagingHealElement(el) {
        const normalized = normalizeWeaponMagingElement(el);
        const w = this.getWeapon()?.weapon;
        if (normalized && !weaponHasNeutralHealForMaging(w)) {
            return this;
        }
        return new Build({ ...this, weaponMagingHealElement: normalized });
    }

    getWeaponMagingHealElement() {
        return this.weaponMagingHealElement ?? null;
    }

    /**
     * Both halves of the forgemage as one value, or null when neither is set —
     * what everything rendering or scoring the weapon is handed.
     * @returns {{damage: string|null, heal: string|null}|null}
     */
    getWeaponMaging() {
        const damage = this.getWeaponMagingElement();
        const heal = this.getWeaponMagingHealElement();
        return damage || heal ? { damage, heal } : null;
    }

    /**
     * The slot's maged lines `{ [characId]: value }`, or null. An exo a
     * generation placed here is a line like any other, and counted as one.
     */
    getItemMaging(slot) {
        const entry = this.itemMaging?.[slot];
        if (!entry) return null;
        if (entry.trans != null) return { ...entry.lines, [TRANS_LINE]: entry.trans };
        return entry.lines || null;
    }

    /** The AP / MP / Range exo lines the slot's item carries, by id. */
    getItemExoIds(slot) {
        const item = this.items?.[slot];
        const lines = this.getItemMaging(slot);
        if (!item || !lines) return [];
        return FM_EXO_IDS.filter((id) => lines[id] && lineKind(item, id, lines[id]) === MAGING_KIND.EXO);
    }

    /** Set one maged line on the slot's item; clamped to the forgemagie rules. */
    setItemMagingLine(slot, characId, value) {
        const item = this.items[slot];
        if (!item || !isSlotMagable(slot)) return this;
        const lines = withLine(item, this.getItemMaging(slot), characId, value);
        const itemMaging = { ...this.itemMaging };
        if (Object.keys(lines).length) itemMaging[slot] = { itemId: this.itemIds[slot], lines };
        else delete itemMaging[slot];
        return new Build({ ...this, itemMaging });
    }

    /** Back to the item as dropped. */
    clearItemMaging(slot) {
        if (!this.itemMaging?.[slot]) return this;
        const itemMaging = { ...this.itemMaging };
        delete itemMaging[slot];
        return new Build({ ...this, itemMaging });
    }

    setCriteriaTags(tagIds) {
        const nextTags = normTagIds(tagIds);
        return new Build({ ...this, criteriaTags: nextTags });
    }

    /**
     * Active spell generator criteria count (UI / solver).
     * @returns {number}
     */
    getSpellSolverTargetCount() {
        return (this.maximizeSpellCriteria || []).length || (this.maximizeSpellIds || []).length;
    }

    /**
     * Active weapon generator criteria count (UI / solver).
     * @returns {number}
     */
    getWeaponSolverTargetCount() {
        return this.maximizeWeapon || this.maximizeBestWeapon ? 1 : 0;
    }

    setCharacterClass(characterClass) {
        // The previous class's boosts would stay picked but unseen: the Boosts
        // tab only lists the new class's, yet the sheet would still count them.
        const breedId = getTypeId(resolveCharacterClass(characterClass)?.name);
        return new Build({
            ...this,
            characterClass,
            boosts: withoutOtherClassBoosts(this.boosts, breedId),
            boostCharges: withoutOtherClassBoosts(this.boostCharges, breedId),
            maximizeSpellIds: [],
            maximizeSpellCriteria: [],
            maximizeWeapon: false,
            maximizeBestWeapon: false,
            weaponMagingElement: null,
            weaponMagingHealElement: null,
        });
    }

    setCharacterSex(characterSex) {
        const nextSex = normalizeCharacterSex(characterSex);
        if (this.characterSex === nextSex) return this;
        return new Build({
            ...this,
            characterSex: nextSex,
        });
    }

    /** Remember how the character is being looked at, so the build reopens the
     *  same way. Returns this when nothing moved, so it does not churn a save. */
    setCharacterView({ stance, facing }) {
        const nextStance = normalizeCharacterStance(stance);
        const nextFacing = normalizeCharacterFacing(facing);
        if (this.characterStance === nextStance && this.characterFacing === nextFacing) return this;
        return new Build({
            ...this,
            characterStance: nextStance,
            characterFacing: nextFacing,
        });
    }

    getCharacterStance() {
        return this.characterStance;
    }

    getCharacterFacing() {
        return this.characterFacing;
    }

    setCharacterRender(characterRender) {
        const nextRender = normalizeCharacterRender(characterRender);
        const current = this.characterRender;
        if (
            current?.key === nextRender?.key
            && current?.dataUrl === nextRender?.dataUrl
            && current?.url === nextRender?.url
        ) {
            return this;
        }
        return new Build({
            ...this,
            characterRender: nextRender,
        });
    }

    removeItem(slotIndex) {
        if (slotIndex < 0 || slotIndex >= this.items.length || !this.items[slotIndex]) {
            return this;
        }

        const newItems = [...this.items];
        newItems[slotIndex] = null;

        let weaponMagingElement = this.weaponMagingElement;
        let weaponMagingHealElement = this.weaponMagingHealElement;
        if (slotIndex === SLOT_IDX.WEAPON) {
            weaponMagingElement = null;
            weaponMagingHealElement = null;
        }

        const newBuild = new Build({
            ...this,
            items: newItems,
            itemIds: newItems.map(resolveItemId),
            weaponMagingElement,
            weaponMagingHealElement,
        });
        return newBuild._recalculateAllSetBonuses();
    }

    /**
     * Recalculates all set bonuses based on current items in the build.
     * Updates pieces count, active_items, and inactive_items for each set.
     * Also detects new sets from items that aren't in setBonuses yet.
     * @returns {Build} New Build instance with updated setBonuses
     */
    _recalculateAllSetBonuses() {
        // With the catalog in memory this is a straight re-derivation from the
        // new equipment. The long path below reconstructs membership by
        // scanning `active_items`/`inactive_items`, which only ever existed
        // because the browser had no other way to learn which items belong to a
        // set; it stays as the fallback for a cold catalog.
        // The constructor derives the block itself whenever the catalog is
        // there, so rebuilding is the whole of the recalculation.
        if (peekItemCatalog()) return new Build({ ...this });

        const newSetBonuses = {};
        const currentItemIds = new Set(
            this.items.filter(item => item && item.id).map(item => String(item.id))
        );

        // First, process existing sets in setBonuses
        if (this.setBonuses && Object.keys(this.setBonuses).length > 0) {
            Object.entries(this.setBonuses).forEach(([setId, setData]) => {
                // Only process sets with the new format
                if (!setData || typeof setData !== 'object' || !setData.bonuses) {
                    newSetBonuses[setId] = setData;
                    return;
                }

                // Get all items that belong to this set (from active + inactive)
                const allSetItems = [
                    ...(setData.active_items || []),
                    ...(setData.inactive_items || [])
                ];

                // Separate into active (in build) and inactive (not in build)
                const activeItems = [];
                const inactiveItems = [];

                allSetItems.forEach(item => {
                    const itemId = item && item.id ? String(item.id) : null;
                    if (itemId && currentItemIds.has(itemId)) {
                        activeItems.push(item);
                    } else {
                        inactiveItems.push(item);
                    }
                });

                // Also check current build items for this set (in case new items were added)
                this.items.forEach(item => {
                    if (item && String(item.item_set_id) === setId) {
                        const itemId = String(item.id);
                        // Only add if not already in activeItems
                        if (!activeItems.some(ai => String(ai.id) === itemId)) {
                            activeItems.push(item);
                        }
                    }
                });

                // Count pieces (only active items count)
                const pieces = activeItems.length;

                // Create updated set info
                const updatedSetInfo = {
                    ...setData,
                    pieces: pieces,
                    active_items: activeItems,
                    inactive_items: inactiveItems,
                    // Keep bonuses array intact (contains all tiers)
                    bonuses: setData.bonuses
                };

                // Only keep sets with at least 2 pieces
                if (pieces >= 2) {
                    newSetBonuses[setId] = updatedSetInfo;
                }
            });
        }

        // Second, detect new sets from items in the build that aren't in setBonuses
        const setsInBuild = new Map();
        this.items.forEach(item => {
            if (item && item.item_set_id) {
                const setId = String(item.item_set_id);
                if (!newSetBonuses[setId]) {
                    // This is a new set not in setBonuses
                    if (!setsInBuild.has(setId)) {
                        setsInBuild.set(setId, {
                            setId: setId,
                            items: [],
                            setName: item.item_set_name || { fr: setId, en: setId }
                        });
                    }
                    setsInBuild.get(setId).items.push(item);
                }
            }
        });

        // Create entries for new sets with at least 2 pieces
        setsInBuild.forEach((setInfo, setId) => {
            const pieces = setInfo.items.length;
            if (pieces >= 2) {
                // Create minimal set entry - bonuses will be empty and need to be fetched
                // But at least the set will be detected and can be displayed
                newSetBonuses[setId] = {
                    name: setInfo.setName,
                    pieces: pieces,
                    active_items: setInfo.items,
                    inactive_items: [],
                    bonuses: [], // Empty - will need to be fetched from server
                    max_pieces: setInfo.items.length // Unknown, use current count as placeholder
                };
            }
        });

        return new Build({ ...this, setBonuses: newSetBonuses });
    }

    _recalculateSetBonuses(setId, currentItems) {
        const setInfo = this.setBonuses[setId];
        if (!setInfo) return this.setBonuses;

        // Count how many items of this set are still in the build
        const currentPieces = currentItems.filter(item => item && item.item_set_id === setId).length;

        // Determine which bonuses are active
        const activeBonuses = [];
        if (setInfo.bonuses) {
            for (let i = 0; i < currentPieces; i++) {
                if (setInfo.bonuses[i]) {
                    activeBonuses.push(setInfo.bonuses[i]);
                }
            }
        }

        // Create a new set info object with updated pieces and active bonuses
        const updatedSetInfo = {
            ...setInfo,
            pieces: currentPieces,
            bonuses: activeBonuses // Replace with only the active bonuses
        };

        // Return new setBonuses object
        return {
            ...this.setBonuses,
            [setId]: updatedSetInfo
        };
    }

    /**
     * The stat sheet, memoised per option set.
     *
     * `solverView` is the sheet the generator's scores read — the one the
     * solver searches from. With `useBoosts` (the generator setting
     * "Utiliser les boosts pour atteindre les critères") every picked boost is
     * a natural stat on it (`getSolverBoostEffects`), a picked item passive
     * replacing that item's packed default (`getSolverPassiveOverrides`);
     * without, it holds no boost and every item passive at its default.
     */
    getStats(options = {}) {
        const includePassiveEffects = options?.includePassiveEffects === true;
        const solverView = options?.solverView === true;
        const useBoosts = options?.useBoosts === true;
        const passiveOverrides = includePassiveEffects ? (options?.passiveOverrides || {}) : {};
        const key = `${includePassiveEffects}|${solverView}|${useBoosts}|${JSON.stringify(passiveOverrides)}`;
        if (!this._statsCache.has(key)) {
            this._statsCache.set(
                key, this._calculateStats({ includePassiveEffects, passiveOverrides, solverView, useBoosts }),
            );
        }
        return this._statsCache.get(key);
    }

    /**
     * `{ characteristicId: points }` of every picked boost, which the solver
     * treats as natural stats of the character: they are added to its base
     * stats (localSolve/kernelRequest.js) the way scrolls are, whatever gear it
     * then picks. Item passives included — the Boosts tab offers those for
     * worn items, and their packed defaults give way (getSolverPassiveOverrides).
     * Only a characteristic the kernel has no column for (Puissance (armes))
     * has nowhere to go. (Boosts never grant AP or MP: the export leaves them out.)
     */
    getSolverBoostEffects() {
        const all = boostEffectsMap(
            this.boosts, this.lvl, getTypeId(this.characterClass?.name), this.boostCharges, this._equippedItemIds(),
        );
        const solverIds = new Set(CHARAC_IDS);
        return Object.fromEntries(Object.entries(all).filter(([id]) => solverIds.has(Number(id))));
    }

    /**
     * Passive overrides with every picked item passive's packed default
     * switched off: the picked value is already a natural stat
     * (getSolverBoostEffects), so the kernel must not add the default too.
     */
    getSolverPassiveOverrides(passiveOverrides = {}) {
        return withPickedItemBoostsDisabled(
            passiveOverrides || {},
            pickedItemBoostIds(this.boosts, this._equippedItemIds()),
        );
    }

    /**
     * `{ characteristicId: points }` the picked item passives' packed defaults
     * add (the Turquoise's 7 %, under `passiveOverrides`): what the kernel still
     * counts on those items when it counts their picked value only for spells.
     */
    getPickedItemPassiveDefaults(passiveOverrides = {}) {
        const picked = this.getBoostedItemIds();
        const out = {};
        (this.items || []).forEach((item) => {
            if (!item || !picked.has(Number(item.id))) return;
            Object.entries(getPassiveEffectLinesMap(item, passiveOverrides) || {}).forEach(([id, value]) => {
                out[id] = (out[id] || 0) + value;
            });
        });
        return out;
    }

    /** Ids of worn items whose passive is picked on the Boosts tab. */
    getBoostedItemIds() {
        return pickedItemBoostIds(this.boosts, this._equippedItemIds());
    }

    _equippedItemIds() {
        return new Set((this.items || []).filter(Boolean).map((item) => Number(item.id)));
    }

    getLvl() {
        return this.lvl;
    }

    getCharacterPoints() {
        return this.characterPoints;
    }

    getCharacterClass() {
        return this.characterClass;
    }

    getCharacterSex() {
        return normalizeCharacterSex(this.characterSex);
    }

    getCharacterRender() {
        return normalizeCharacterRender(this.characterRender);
    }

    /**
     * Elemental tint for gradients, shadows and the skin render's accent.
     *
     * Derived data always loses to real data: whenever this build actually
     * carries equipment the tint is computed from it, and the stored value is
     * consulted only when there is nothing to compute from — which is precisely
     * the community-card case, where the listing strips items to save egress.
     * That ordering is what makes the stored tint impossible to go stale: an
     * edited build has items, so its stored value is never the one used.
     * @returns {{r: number, g: number, b: number, elementTotal: number}}
     */
    getElementTint() {
        const hasItems = Array.isArray(this.items) && this.items.some(Boolean);
        if (!hasItems) {
            if (this.elementTint) return this.elementTint;
            // Builds saved before elementTint existed still carry statSheet —
            // the flat {statId: value} sheet the OG cards already use, and a
            // form calculateBlendedColor accepts directly. Reading it here is
            // what keeps older cards coloured without a migration.
            if (this.statSheet && typeof this.statSheet === 'object') {
                if (!this._cachedElementTint) {
                    const { r, g, b } = calculateBlendedColor(this.statSheet);
                    this._cachedElementTint = {
                        r, g, b,
                        elementTotal: ELEMENT_STAT_IDS.reduce(
                            (sum, id) => sum + (Number(this.statSheet[id]) || 0), 0,
                        ),
                    };
                }
                return this._cachedElementTint;
            }
        }
        if (!this._cachedElementTint) {
            const stats = this.getStats();
            const { r, g, b } = calculateBlendedColor(stats);
            const elementTotal = ELEMENT_STAT_IDS
                .reduce((sum, id) => sum + (stats?.[id]?.current || 0), 0);
            this._cachedElementTint = { r, g, b, elementTotal };
        }
        return this._cachedElementTint;
    }

    reset() {
        return new Build();
    }

    _calculateStats(options = {}) {
        const includePassiveEffects = options?.includePassiveEffects === true;
        const equippedItemIds = this._equippedItemIds();
        // An item whose passive is picked on the Boosts tab loses its fixed
        // default passive here, so the two are not both counted — on the sheet
        // always, and on the solver's sheet when it counts boosts at all.
        const boostsOnSheet = !options?.solverView || options?.useBoosts === true;
        const passiveOverrides = boostsOnSheet
            ? this.getSolverPassiveOverrides(options?.passiveOverrides || {})
            : (options?.passiveOverrides || {});
        if (!this.attributes) return null;
        const stats = {};
        const naturalStats = st.getNaturalStats(this.lvl);
        st.STATS.forEach(stat => {
            if (stat.id === st.LVL_ID) {
                stats[stat.id] = {
                    ...stat,
                    base: this.lvl,
                    scroll: 0,
                    desired: this.lvl,
                    current: this.lvl,
                    maximize: false,
                };
                return;
            }
            let scrollValue = this.scrolls[stat.id] || 0;
            let attrValue = this.attributes[stat.id] || 0;
            let naturalValue = naturalStats[stat.id] || 0;
            // null = no target on this characteristic.
            let desiredValue = this.desired[stat.id] ?? null;

            let baseTotal = attrValue + naturalValue + scrollValue;
            // The tooltip names where each point came from, so scrolls get
            // their own row instead of hiding inside "Base".
            const breakdown = [];
            if (attrValue + naturalValue !== 0) breakdown.push({ name: TXT_BASE, value: attrValue + naturalValue });
            if (scrollValue !== 0) breakdown.push({ name: TXT_SCROLLS, value: scrollValue });
            stats[stat.id] = {
                ...stat,
                base: attrValue,
                scroll: scrollValue,
                desired: desiredValue,
                current: baseTotal,
                maximize: !!this.maximizeTargets[stat.id],
                breakdown,
            };
        });
        this.items.forEach((item, slot) => {
            if (!item) return;
            const lines = this.getItemMaging(slot);
            const effectMap = lines ? magedEffectsMap(item, lines) : getItemDisplayEffectsMap(item);
            if (effectMap) {
                addItemStatsFromMap(stats, effectMap, item);
            } else if (Array.isArray(item.effects)) {
                // Legacy fallback for older payloads without effects_map.
                addItemStatsFromMap(stats, getEffectLinesMap(item.effects), item);
            }

            if (includePassiveEffects) {
                // Backend solver includes passive combat effects in item stats.
                // Keep this opt-in so UI tabs continue showing ordinary stats.
                addItemStatsFromMap(
                    stats,
                    getPassiveEffectLinesMap(item, passiveOverrides),
                    item,
                    { fr: 'Effet passif', en: 'Passive effect', de: 'Passiver Effekt', es: 'Efecto pasivo', pt: 'Efeito passivo' }
                );
            }
        });
        // The game allows +1 AP, MP and Range of forgemagie in total. Points
        // maged past it stay on the item — the user may set them — but the
        // sheet takes them back off, with a row saying why.
        const itemDeltas = this.items
            .map((item, slot) => {
                const lines = item ? this.getItemMaging(slot) : null;
                return lines ? magingDelta(item, lines) : null;
            })
            .filter(Boolean);
        Object.entries(magingBonusExcess(null, itemDeltas)).forEach(([statId, excess]) => {
            const stat = stats[statId];
            if (!stat) return;
            stat.current -= excess;
            stat.breakdown.push({ name: TXT_MAGING_CAP, value: -excess });
        });

        // Ceilings the equipped sets put on a characteristic, collected while the
        // tiers are walked and applied once everything else has been summed —
        // see backend `core/set_caps.py` and `stats.rs::apply_set_caps`.
        const setCaps = new Map();
        const noteCap = (characteristic, value, setName) => {
            const current = setCaps.get(characteristic);
            if (current === undefined || value < current.value) {
                setCaps.set(characteristic, { value, setName });
            }
        };

        Object.values(this.setBonuses).forEach(setData => {
            let bonusesToProcess;

            // Check if this is the new format with name, pieces, bonuses structure
            if (setData && typeof setData === 'object' && setData.bonuses) {
                const pieces = setData.pieces || 0;
                const allBonuses = setData.bonuses;
                bonusesToProcess = [];
                if (Array.isArray(allBonuses) && pieces >= 2) {
                    // The server sends cumulative bonuses for each tier.
                    // We only need the bonus for the highest tier achieved.
                    // Bonuses are 0-indexed for 2+ pieces, so `pieces - 2` is the correct index.
                    const currentTierBonus = allBonuses[pieces - 2];
                    if (currentTierBonus) {
                        bonusesToProcess.push(currentTierBonus);
                    }
                }
                // A build whose tiers were backfilled server-side gets its ceilings
                // in a parallel list rather than inside the tier — see the backend
                // `transform_set_caps`. Indexed the same way.
                if (Array.isArray(setData.bonus_caps) && pieces >= 2) {
                    (setData.bonus_caps[pieces - 2] || []).forEach(cap => {
                        const id = cap?.characteristic;
                        const value = Number(cap?.value);
                        if (stats[id] && Number.isFinite(value)) noteCap(id, value, setData.name);
                    });
                }
            } else {
                // Fallback for old format
                bonusesToProcess = setData;
            }

            // Process bonuses (handle both new and old formats)
            if (Array.isArray(bonusesToProcess)) {
                bonusesToProcess.forEach(bonusTier => {
                    if (bonusTier && typeof bonusTier === 'object' && Array.isArray(bonusTier.caps)) {
                        bonusTier.caps.forEach(cap => {
                            const id = cap?.characteristic;
                            const value = Number(cap?.value);
                            if (stats[id] && Number.isFinite(value)) noteCap(id, value, setData.name);
                        });
                    }
                    // Handle new format: { num_items_required: X, effects: [...] }
                    if (bonusTier && typeof bonusTier === 'object' && bonusTier.effects && Array.isArray(bonusTier.effects)) {
                        bonusTier.effects.forEach(bonusEffect => {
                            if (bonusEffect && stats[bonusEffect.characteristic]) {
                                const id = bonusEffect.characteristic;
                                // Handle both { value: X } and { min: X, max: Y } formats
                                const value = bonusEffect.value !== undefined
                                    ? bonusEffect.value
                                    : (bonusEffect.min !== undefined ? bonusEffect.min : (bonusEffect.max !== undefined ? bonusEffect.max : 0));
                                if (value !== 0) {
                                    stats[id].current += value;
                                    stats[id].breakdown.push({
                                        name: setData.name || { fr: 'Bonus de Panoplie', en: 'Set Bonus', de: 'Set-Bonus', es: 'Bonus de set', pt: 'Bônus de conjunto' },
                                        value: value
                                    });
                                }
                            }
                        });
                    }
                    // Handle old format: array of effects directly
                    else if (Array.isArray(bonusTier)) {
                        bonusTier.forEach(bonusEffect => {
                            if (bonusEffect && stats[bonusEffect.characteristic]) {
                                const id = bonusEffect.characteristic;
                                const value = bonusEffect.value;
                                if (value !== 0) {
                                    stats[id].current += value;
                                    stats[id].breakdown.push({
                                        name: setData.name || { fr: 'Bonus de Panoplie', en: 'Set Bonus', de: 'Set-Bonus', es: 'Bonus de set', pt: 'Bônus de conjunto' },
                                        value: value
                                    });
                                }
                            }
                        });
                    }
                });
            }
        });

        // Boosts: characteristics the character casts onto themselves before the
        // fight. They land after items and sets because they are neither — the
        // sheet is what they change, and a boost can name a characteristic no
        // item carries (Puissance (armes) is the whole reason this exists).
        const boostEffects = options?.solverView
            ? (options?.useBoosts === true ? this.getSolverBoostEffects() : {})
            : boostEffectsMap(
                this.boosts, this.lvl, getTypeId(this.characterClass?.name), this.boostCharges, equippedItemIds,
            );
        Object.entries(boostEffects).forEach(([statId, value]) => {
            const stat = stats[statId];
            if (!stat || !value) return;
            stat.current += value;
            stat.breakdown.push({ name: TXT_BOOSTS, value });
        });

        // Set ceilings land last, after items, sets and boosts: the game caps the
        // characteristic itself, so nothing that raised it escapes. The shortfall
        // gets its own breakdown row — a sheet that silently reads 2 PM while the
        // rows above it add up to 4 is the confusing half of this mechanic.
        setCaps.forEach(({ value, setName }, statId) => {
            const stat = stats[statId];
            if (!stat || stat.current <= value) return;
            stat.breakdown.push({ name: setName || TXT_SET_CAP, value: value - stat.current });
            stat.current = value;
        });
        // Then the game's own ceilings (12 AP, 6 MP, 50% resistance...), the
        // same way: the sheet reads what the character actually has.
        Object.entries(st.GAME_STAT_CAPS).forEach(([statId, value]) => {
            const stat = stats[statId];
            if (!stat || stat.current <= value) return;
            stat.breakdown.push({ name: TXT_GAME_CAP, value: value - stat.current });
            stat.current = value;
        });

        if (includePassiveEffects && stats[st.FINAL_DAMAGE_ID]) {
            const turquoiseItems = this.items.filter((item) => Number(item?.id) === TURQUOISE_DOFUS_ITEM_ID);
            if (turquoiseItems.length) {
                const staticFinalDamage = turquoiseItems.reduce(
                    (sum, item) => sum + getPassiveEffectValue(item, st.FINAL_DAMAGE_ID, passiveOverrides),
                    0
                );
                const scaledFinalDamage = turquoiseItems.reduce(
                    (sum, item) => sum + turquoiseFinalDamageForCritical(
                        stats[st.CRIT_ID]?.current || 0,
                        getPassiveEffectValue(item, st.FINAL_DAMAGE_ID, passiveOverrides)
                    ),
                    0
                );
                const adjustment = scaledFinalDamage - staticFinalDamage;
                if (adjustment !== 0) {
                    stats[st.FINAL_DAMAGE_ID].current += adjustment;
                    stats[st.FINAL_DAMAGE_ID].breakdown.push({
                        name: { fr: 'Dofus Turquoise ajusté aux Critiques', en: 'Turquoise Dofus adjusted by Critical', de: 'Türkis-Dofus an Kritisch angepasst', es: 'Dofus Turquesa ajustado a los Críticos', pt: 'Dofus Turquesa ajustado aos Críticos' },
                        value: adjustment
                    });
                }
            }
        }

        // Add initiative from elemental stats
        const initiativeFromStats = (stats[st.STRENGTH_ID]?.current || 0) +
            (stats[st.INTELLIGENCE_ID]?.current || 0) +
            (stats[st.CHANCE_ID]?.current || 0) +
            (stats[st.AGILITY_ID]?.current || 0);

        if (stats[st.INITIATIVE_ID] && initiativeFromStats !== 0) {
            stats[st.INITIATIVE_ID].current += initiativeFromStats;
            stats[st.INITIATIVE_ID].breakdown.push({ name: { fr: 'Caractéristiques', en: 'Stats', de: 'Eigenschaften', es: 'Características', pt: 'Características' }, value: initiativeFromStats });
        }

        // Add AP/MP reduction and parry from wisdom
        const wisdomBonus = Math.floor((stats[st.WISDOM_ID]?.current || 0) / 10);
        if (wisdomBonus > 0) {
            if (stats[st.AP_REDUCTION_ID]) {
                stats[st.AP_REDUCTION_ID].current += wisdomBonus;
                stats[st.AP_REDUCTION_ID].breakdown.push({ name: { fr: 'Sagesse', en: 'Wisdom', de: 'Weisheit', es: 'Sabiduría', pt: 'Sabedoria' }, value: wisdomBonus });
            }
            if (stats[st.MP_REDUCTION_ID]) {
                stats[st.MP_REDUCTION_ID].current += wisdomBonus;
                stats[st.MP_REDUCTION_ID].breakdown.push({ name: { fr: 'Sagesse', en: 'Wisdom', de: 'Weisheit', es: 'Sabiduría', pt: 'Sabedoria' }, value: wisdomBonus });
            }
            if (stats[st.AP_PARRY_ID]) {
                stats[st.AP_PARRY_ID].current += wisdomBonus;
                stats[st.AP_PARRY_ID].breakdown.push({ name: { fr: 'Sagesse', en: 'Wisdom', de: 'Weisheit', es: 'Sabiduría', pt: 'Sabedoria' }, value: wisdomBonus });
            }
            if (stats[st.MP_PARRY_ID]) {
                stats[st.MP_PARRY_ID].current += wisdomBonus;
                stats[st.MP_PARRY_ID].breakdown.push({ name: { fr: 'Sagesse', en: 'Wisdom', de: 'Weisheit', es: 'Sabiduría', pt: 'Sabedoria' }, value: wisdomBonus });
            }
        }

        // Add lock and dodge from agility
        const agilityBonus = Math.floor((stats[st.AGILITY_ID]?.current || 0) / 10);
        if (agilityBonus > 0) {
            if (stats[st.DODGE_ID]) {
                stats[st.DODGE_ID].current += agilityBonus;
                stats[st.DODGE_ID].breakdown.push({ name: { fr: 'Agilité', en: 'Agility', de: 'Flinkheit', es: 'Agilidad', pt: 'Agilidade' }, value: agilityBonus });
            }
            if (stats[st.LOCK_ID]) {
                stats[st.LOCK_ID].current += agilityBonus;
                stats[st.LOCK_ID].breakdown.push({ name: { fr: 'Agilité', en: 'Agility', de: 'Flinkheit', es: 'Agilidad', pt: 'Agilidade' }, value: agilityBonus });
            }
        }

        // Add prospecting from chance
        const prospectingBonus = Math.floor((stats[st.CHANCE_ID]?.current || 0) / 10);
        if (prospectingBonus > 0) {
            if (stats[st.PROSPECTING_ID]) {
                stats[st.PROSPECTING_ID].current += prospectingBonus;
                stats[st.PROSPECTING_ID].breakdown.push({ name: { fr: 'Chance', en: 'Chance', de: 'Glück', es: 'Suerte', pt: 'Sorte' }, value: prospectingBonus });
            }
        }

        // Add pods from strength
        const podsBonus = (stats[st.STRENGTH_ID]?.current || 0) * 5;
        if (podsBonus > 0) {
            if (stats[st.PODS_ID]) {
                stats[st.PODS_ID].current += podsBonus;
                stats[st.PODS_ID].breakdown.push({ name: { fr: 'Force', en: 'Strength', de: 'Stärke', es: 'Fuerza', pt: 'Força' }, value: podsBonus });
            }
        }

        return stats;
    }

    /**
     * Returns the effective minimum value for a stat, given the stat object and level.
     * Special case: for AP, if lvl >= 100, min is minRange + 1; else minRange.
     */
    static getEffectiveMinRange(stat, lvl) {
        if (stat.id === st.AP_ID) {
            return lvl >= 100 ? stat.minRange + 1 : stat.minRange;
        }
        return stat.minRange;
    }

    /**
     * Returns the effective maximum value for a stat, given the stat object and level.
     * No special cases.
     */
    static getEffectiveMaxRange(stat, lvl) {
        return stat.maxRange;
    }

    /**
     * Clears every characteristic target, numeric and maximize.
     * @returns {Build} A new Build when changes are applied, otherwise current instance.
     */
    resetActiveTargets() {
        const hadTargets = Object.keys(this.desired).length > 0;
        const hadMaximize = Object.keys(this.maximizeTargets || {}).length > 0;
        if (!hadTargets && !hadMaximize) return this;
        return new Build({
            ...this,
            desired: {},
            maximizeTargets: {},
        });
    }

    /**
     * Clears all equipped item slots and related set bonuses.
     * @returns {Build} A new Build instance with empty slots.
     */
    clearBuildSlots() {
        const slotCount = Array.isArray(this.items) ? this.items.length : TOTAL_SLOTS;
        const emptyItems = Array(slotCount).fill(null);
        // Through `setItems`, not a bare spread: `itemIds` is what the
        // constructor believes, so a spread that empties `items` alone would
        // carry the old ids and put every item straight back. `setItems` also
        // drops the weapon maging and re-derives the set bonuses, which is the
        // rest of what clearing means.
        return this.setItems(emptyItems);
    }

    /**
     * Clears all unlocked slots while preserving items in locked slots.
     * @param {number[]|Object} lockedSlotIds - Array of locked slot indices or an object whose keys are locked slot indices.
     * @returns {Build} A new Build instance with only locked slots preserved.
     */
    clearUnlockedSlots(lockedSlotIds = []) {
        const items = Array.isArray(this.items) ? this.items : Array(TOTAL_SLOTS).fill(null);

        // Normalize locked slots input to a Set of numeric indices
        const lockedSet = new Set(
            (Array.isArray(lockedSlotIds) ? lockedSlotIds : Object.keys(lockedSlotIds || {}))
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id))
        );

        const newItems = items.map((item, idx) => (lockedSet.has(idx) ? item : null));
        return this.setItems(newItems);
    }

    getRemainingCharacterPoints() {
        const totalPoints = st.getTotalCharacterPoints(this.lvl);
        const investedCost = Object.entries(this.attributes).reduce((cost, [statId, value]) => {
            const statIdNum = Number(statId);
            if (st.ATTRIBUTES.includes(statIdNum)) {
                return cost + st.calculateTotalCost(statIdNum, value);
            }
            return cost;
        }, 0);
        return totalPoints - investedCost;
    }

    setBaseAttribute(statId, desiredValue) {
        const desiredNumericValue = Number(desiredValue) || 0;
        const remainingPoints = this.getRemainingCharacterPoints();
        const currentValue = this.attributes[statId] || 0;
        const currentCost = st.calculateTotalCost(statId, currentValue);
        const pointsAvailableForThisStat = remainingPoints + currentCost;
        const maxAchievableValue = st.investedPointsToStatValue(this.lvl, statId, pointsAvailableForThisStat);
        const finalValue = Math.min(desiredNumericValue, maxAchievableValue);
        const newAttributes = { ...this.attributes, [statId]: finalValue };
        return new Build({ ...this, attributes: newAttributes });
    }

    getHat() { return Array.isArray(this.items) ? this.items[SLOT_IDX.HAT] || null : null; }
    getCloak() { return Array.isArray(this.items) ? this.items[SLOT_IDX.CLOAK] || null : null; }
    getAmulet() { return Array.isArray(this.items) ? this.items[SLOT_IDX.AMULET] || null : null; }
    getRingL() { return Array.isArray(this.items) ? this.items[SLOT_IDX.RING1] || null : null; }
    getRingR() { return Array.isArray(this.items) ? this.items[SLOT_IDX.RING2] || null : null; }
    getBelt() { return Array.isArray(this.items) ? this.items[SLOT_IDX.BELT] || null : null; }
    getBoots() { return Array.isArray(this.items) ? this.items[SLOT_IDX.BOOTS] || null : null; }
    getWeapon() { return Array.isArray(this.items) ? this.items[SLOT_IDX.WEAPON] || null : null; }
    getShield() { return Array.isArray(this.items) ? this.items[SLOT_IDX.SHIELD] || null : null; }
    getPet() { return Array.isArray(this.items) ? this.items[SLOT_IDX.PET] || null : null; }
    getTrophus() {
        if (!Array.isArray(this.items)) return [];
        return SLOT_IDX.TROPHUS.map(idx => this.items[idx]);
    }

    /**
     * Returns a normalized info object for an item, including icon, name, set, level, stats, and conditions.
     * @param {Object} item - The item object from the build.
     * @param {string} lang - 'en' or 'fr'.
     * @returns {Object|null}
     */
    getItemInfo(item, lang = 'en') {
        if (!item) return null;
        // Not every item reaching here carries its full text: a trimmed payload
        // or an unresolved catalog id arrives without `name`, and indexing that
        // by language is what crashed the tooltip. See utils/localizedText.
        const name = localizedText(item.name, lang);

        // Get set name from item_set_name properties
        let setName = null;
        if (item.item_set_id) {
            setName = localizedText(item.item_set_name, lang, item.item_set_id);
        }

        const lvl = item.lvl || item.level || item.lvl_min || null;
        const icon = item.icon || (item.type ? item.type.img : null);
        // Build stats list from effects_map
        let stats = [];
        if (item.effects_map) {
            for (const [statId, value] of Object.entries(item.effects_map)) {
                const stat = st.STATS.find(s => String(s.id) === String(statId));
                if (stat) {
                    const label = localizedText(stat.label, lang);
                    stats.push(`${label}: ${value > 0 ? '+' : ''}${value}`);
                } else {
                    stats.push(`Stat ${statId}: ${value > 0 ? '+' : ''}${value}`);
                }
            }
        }
        // Build conditions string from criteria
        let conditions = null;
        conditions = processItemRequirementText(item, lang, this.lvl);
        return {
            icon,
            name,
            set: setName ? { name: setName } : null,
            lvl,
            stats,
            conditions,
        };
    }

    /**
     * Returns structured set information including name, pieces count, and bonuses.
     * @returns {Array} Array of set objects with id, name, pieces, and bonuses.
     */
    getSetInfo(lang = 'fr') {
        if (!this.setBonuses) return [];

        const sets = [];
        Object.entries(this.setBonuses).forEach(([setId, setData]) => {
            // Handle new format: { name, pieces, bonuses, active_items, inactive_items, max_pieces } or old format: array of bonus arrays
            let setName, pieces, bonusList, activeItems, inactiveItems, maxPieces;

            if (setData && typeof setData === 'object' && setData.bonuses) {
                // `bonuses` being present says nothing about `name`: both
                // serializers carry the localized dict now, but an old stored
                // block can still hold a bare string and a set the catalog
                // missed carries neither. Guarded rather than routed through
                // localizedText, whose chain prefers en over fr.
                setName = (setData.name && (setData.name[lang] || setData.name.fr || setData.name.en))
                    || setData.display_name || setId;
                pieces = setData.pieces || 0;
                const allBonuses = setData.bonuses;
                bonusList = [];

                if (Array.isArray(allBonuses) && pieces >= 2) {
                    // We only need the bonus for the highest tier achieved.
                    const currentTierBonus = allBonuses[pieces - 2];
                    if (currentTierBonus) {
                        bonusList.push(currentTierBonus);
                    }
                }

                activeItems = setData.active_items || [];
                inactiveItems = setData.inactive_items || [];
                maxPieces = setData.max_pieces || 0;
            } else {
                // Old format - fallback
                setName = setId;
                pieces = 0;
                bonusList = setData;
                activeItems = [];
                inactiveItems = [];
                maxPieces = 0;
            }

            if (!Array.isArray(bonusList)) return;

            // Find items that belong to this set by matching set ID (fallback for old format)
            const setItems = this.items.filter(item => {
                return item && String(item.item_set_id) === String(setId);
            });

            sets.push({
                id: setId,
                name: setName,
                pieces: pieces,
                max_pieces: maxPieces,
                items: setItems, // Keep for backward compatibility
                active_items: activeItems,
                inactive_items: inactiveItems,
                bonuses: bonusList
            });
        });

        return sets;
    }
} 
